import { BASE_URL } from "config/urls";
import { deleteCall, post, useFetch } from "utils/sdk";

export interface IDetailOpenAIDocumentAssistant {
  id: number;
  deal_id: number;
  file: {
    id: number;
    url: string;
    original_file_name: string;
  };
  openai_document_type: number;
}

export interface IListOpenAIDocumentAssistant
  extends IDetailOpenAIDocumentAssistant {
  prompt_response_count: number;
}

export interface IOpenAICustomPromptResponse {
  id: number;
  response: string;
  updated_at: string;
  updated_by: {
    first_name: string;
    last_name: string;
  };
}

export interface IOpenAIPrompt {
  question: string;
}

export interface IOpenAIPromptResponse {
  id: number;
  readable_answer: string;
  created_at: string;
}

export interface IOpenAIPrompt {
  id: number;
  question: string;
  prompt_responses: Array<IOpenAIPromptResponse>;
  custom_responses: Array<IOpenAICustomPromptResponse>;
}

export const openAIDocumentAssistantCreate = ({
  fileId,
  dealId,
}: {
  fileId: number;
  dealId: number;
}) =>
  post<string>(`${BASE_URL}v2/integrations/openai/document-assistant/create/`, {
    file_id: fileId,
    deal_id: dealId,
  });

export const openAIDocumentAssistantDelete = ({
  assistantId,
}: {
  assistantId: number;
}) =>
  deleteCall(
    `${BASE_URL}v2/integrations/openai/document-assistant/${assistantId}/delete/`
  );

export const useOpenAIPromptList = () =>
  useFetch<Array<IOpenAIPrompt>>("v2/integrations/openai/prompt/list/");

export const useOpenAIDocumentAssistantList = ({
  dealId,
}: {
  dealId: number;
}) =>
  useFetch<Array<IListOpenAIDocumentAssistant>>(
    `v2/integrations/openai/deal/${dealId}/document-assistant/list/`
  );

export const useOpenAIDocumentAssistant = ({
  openAIDocumentAssistantId,
}: {
  openAIDocumentAssistantId: number;
}) =>
  useFetch<IDetailOpenAIDocumentAssistant>(
    `v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/retrieve/`
  );

export interface IDocumentType {
  id: number;
  name: string;
}

export const useOpenAIDocumentTypes = () =>
  useFetch<Array<IDocumentType>>("v2/integrations/openai/document-type/list/");

export const generateAnswer = ({
  openAIDocumentAssistantId,
  promptId,
}: {
  openAIDocumentAssistantId: number;
  promptId: number;
}) =>
  post(
    `${BASE_URL}v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/generate-answer/`,
    {
      prompt: promptId,
    }
  );

export const promptResponseDelete = ({
  openAIDocumentAssistantId,
  promptResponseId,
}: {
  openAIDocumentAssistantId: number;
  promptResponseId: number;
}) =>
  deleteCall(
    `${BASE_URL}v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/prompt-response/${promptResponseId}/delete/`
  );

export const usePromptList = ({
  openAIDocumentAssistantId,
}: {
  openAIDocumentAssistantId: number;
}) =>
  useFetch<Array<IOpenAIPrompt>>(
    `v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/prompt/list/`
  );

export const openAIDocumentAssistantDocumentTypeUpdate = ({
  openAIDocumentAssistantId,
  openAIDocumentTypeId,
}: {
  openAIDocumentAssistantId: number;
  openAIDocumentTypeId: number;
}) =>
  post(
    `${BASE_URL}v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/document-type/update/`,
    { openai_document_type: openAIDocumentTypeId }
  );

export const openAIPromptCustomResponseCreateOrUpdate = ({
  openAIDocumentAssistantId,
  promptId,
  response,
}: {
  openAIDocumentAssistantId: number;
  promptId: number;
  response: string;
}) =>
  post(
    `${BASE_URL}v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/prompt/${promptId}/custom-prompt-response/create-or-update/`,
    { response }
  );

export const openAICustomPromptResponseDelete = ({
  openAIDocumentAssistantId,
  customPromptResponseId,
}: {
  openAIDocumentAssistantId: number;
  customPromptResponseId: number;
}) =>
  deleteCall(
    `${BASE_URL}v2/integrations/openai/document-assistant/${openAIDocumentAssistantId}/custom-prompt-response/${customPromptResponseId}/delete/`
  );
